<template>
   <!--Page alert + loader-->
   <PageStatus :alert="defaultAlert" :table-data="tableData" />


   <!--Import Data-->
   <ImportModal
      :form-import-data="formImportData"
      :alert="defaultAlert"
      errors-title="Problems and notes"
      @file-change="onFileImportChange"
      @import="importRecords"
      @download-template="downloadImportTemplate"
   >
      <!--Import Notes-->
      <div id="accordion">
         <div class="card">
            <div class="card-header">
               <a class="btn" data-bs-toggle="collapse" href="#collapseOne">
                  Import Process
               </a>
            </div>
            <div id="collapseOne" class="collapse show" data-bs-parent="#accordion">
               <div class="card-body">
                  <ol>
                     <li>Roles belong to the <b>company you are logged into</b>. Switch to the right company before importing.</li>
                     <li>Click <b>Download Import Template</b> for the right columns, or use a file from <b>Export to Excel</b>: it has the same columns.</li>
                     <li><b>Role name</b> is required and must be new in your company. Names that already exist are skipped (deleted roles count too).</li>
                     <li><b>Permissions</b>: permission <b>keys</b> separated by commas, e.g. <code>users.view, crudtest.view</code>. Keys are the same on every server; see the Permissions page for them. Leave it empty to assign permissions after importing.</li>
                     <li>Only permissions in your <b>plan</b> are kept. Unknown keys and keys outside your plan are left out, and the role is still imported. They are listed after the import.</li>
                  </ol>
               </div>
            </div>
         </div>
      </div>
      <!--End Import Notes-->
   </ImportModal>
   <!--Import Data-->

      <!--Filter Data Modal (shared: FilterModal.vue)-->
   <FilterModal
      :draft="filterDraft"
      v-model:search-by="tableData.searchBy"
      :view-options="viewOptions"
      :search-by-options="searchByOptions"
      :alert="defaultAlert"
      @search-by-change="changePlaceholder()"
      @apply="applyFilters"
      @clear="clearFilters"
   />
   <!--End Filter Data Modal-->

   
      <!-- The Modal -->
   <RecordFormModal
      :title="isEditing ? 'Update Role' : 'Add Role'"
      title-icon="bi bi-person-badge"
      size="lg"
      :is-editing="isEditing"
      :is-saving="isSaving"
      :alert="defaultAlert"
      @save="saveOrEditRecord"
      @sample="addSampleData"
      @reset="resetForm"
   >
      <!--Below id data id. User id is accessed directly in computed properties inside formData_fields function-->
      <HiddenField v-model="formInputs.id" />

      <!-- Row 1: Role -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.role"
            col="col-md-12"
            label="Role"
            icon="fa fa-user-secret"
            placeholder="e.g. Sales Manager"
            tooltip="A new role name, e.g. Sales Manager"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
      </div>

      <!-- Row 2: Permissions picker (Available ⇄ Assigned) -->
      <div class="row g-3 mb-0">
         <div class="col-md-12">
            <div class="mb-0 position-relative">
               <label class="form-label">
                  <i class="fa fa-shield me-1"></i>Permissions
                  <small v-if="submitted && !assignedPermissions.length" class="text-danger ms-2">Assign at least one permission</small>
               </label>

               <!-- $validateForm checks this: empty while nothing is assigned -->
               <input
                  type="hidden"
                  name="permissions"
                  :value="formInputs.permissions.length || ''"
                  required
               />

               <div class="row g-2">

                  <!-- Available Permissions -->
                  <div class="col-md-5">
                     <div class="card h-100 mb-0 sa-picker-card">
                        <div class="card-header py-2">
                           <strong>Available Permissions</strong>
                        </div>

                        <div class="card-body p-2 overflow-auto" style="max-height: 400px;">
                           <div
                              v-for="(permissions, categoryName) in groupedAvailablePermissions"
                              :key="categoryName"
                              class="mb-2"
                           >
                              <!-- Category -->
                              <div
                                 class="d-flex align-items-center justify-content-between px-2 py-2 rounded"
                                 style="cursor: pointer; background: rgba(var(--bs-primary-rgb, 13, 110, 253), 0.08);"
                              >
                                 <div class="d-flex align-items-center">
                                    <input
                                       type="checkbox"
                                       class="form-check-input me-2"
                                       :checked="isAvailablePermissionCategorySelected(permissions)"
                                       :ref="el => setAvailableCategoryCheckboxState(el, permissions)"
                                       @click.stop
                                       @change="toggleAvailablePermissionCategorySelection(permissions)"
                                    />
                                    <span
                                       class="fw-semibold text-info"
                                       @click="toggleAvailablePermissionCategory(categoryName)"
                                    >
                                       {{ categoryName }}
                                       <span class="text-muted small">
                                          ({{ permissions.length }})
                                       </span>
                                    </span>
                                 </div>

                                 <i
                                    class="fa"
                                    :class="isAvailablePermissionCategoryExpanded(categoryName)
                                       ? 'fa-chevron-down'
                                       : 'fa-chevron-right'"
                                    @click="toggleAvailablePermissionCategory(categoryName)"
                                 ></i>
                              </div>

                              <!-- Permissions -->
                              <transition name="permission-expand">
                                 <div
                                    v-if="isAvailablePermissionCategoryExpanded(categoryName)"
                                    class="mt-2"
                                 >
                                    <div
                                       v-for="permission in permissions"
                                       :key="permission.id"
                                       class="form-check mb-1 ms-2"
                                    >
                                       <input
                                          :id="'available-permission-' + permission.id"
                                          type="checkbox"
                                          class="form-check-input"
                                          :checked="isAvailablePermissionSelected(permission.id)"
                                          @change="toggleAvailablePermission(permission)"
                                       />
                                       <label
                                          :for="'available-permission-' + permission.id"
                                          class="form-check-label"
                                       >
                                          {{ permission.name }}
                                       </label>
                                    </div>
                                 </div>
                              </transition>
                           </div>

                           <div
                              v-if="availablePermissions.length === 0"
                              class="text-muted small"
                           >
                              No available permissions.
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Arrows -->
                  <div class="col-md-2 d-flex flex-column justify-content-center align-items-center gap-2">
                     <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        @click="moveSelectedPermissionsToAssigned"
                     >
                        <i class="fa fa-arrow-right"></i>
                     </button>
                     <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="moveSelectedPermissionsToAvailable"
                     >
                        <i class="fa fa-arrow-left"></i>
                     </button>
                  </div>

                  <!-- Assigned Permissions -->
                  <div class="col-md-5">
                     <div class="card h-100 mb-0 sa-picker-card">
                        <div class="card-header py-2">
                           <strong>Assigned Permissions</strong>
                        </div>

                        <div class="card-body p-2 overflow-auto" style="max-height: 400px;">
                           <div
                              v-for="(permissions, categoryName) in groupedAssignedPermissions"
                              :key="categoryName"
                              class="mb-2"
                           >
                              <!-- Category -->
                              <div
                                 class="d-flex align-items-center justify-content-between px-2 py-2 rounded"
                                 style="cursor: pointer; background: rgba(var(--bs-primary-rgb, 13, 110, 253), 0.08);"
                              >
                                 <div class="d-flex align-items-center">
                                    <input
                                       type="checkbox"
                                       class="form-check-input me-2"
                                       :checked="isAssignedPermissionCategorySelected(permissions)"
                                       :ref="el => setAssignedCategoryCheckboxState(el, permissions)"
                                       @click.stop
                                       @change="toggleAssignedPermissionCategorySelection(permissions)"
                                    />
                                    <span
                                       class="fw-semibold text-info"
                                       @click="toggleAssignedPermissionCategory(categoryName)"
                                    >
                                       {{ categoryName }}
                                       <span class="text-muted small">
                                          ({{ permissions.length }})
                                       </span>
                                    </span>
                                 </div>

                                 <i
                                    class="fa"
                                    :class="isAssignedPermissionCategoryExpanded(categoryName)
                                       ? 'fa-chevron-down'
                                       : 'fa-chevron-right'"
                                    @click="toggleAssignedPermissionCategory(categoryName)"
                                 ></i>
                              </div>

                              <!-- Permissions -->
                              <transition name="permission-expand">
                                 <div
                                    v-if="isAssignedPermissionCategoryExpanded(categoryName)"
                                    class="mt-2"
                                 >
                                    <div
                                       v-for="permission in permissions"
                                       :key="permission.id"
                                       class="form-check mb-1 ms-2"
                                    >
                                       <input
                                          :id="'assigned-permission-' + permission.id"
                                          type="checkbox"
                                          class="form-check-input"
                                          :checked="isAssignedPermissionSelected(permission.id)"
                                          @change="toggleAssignedPermission(permission)"
                                       />
                                       <label
                                          :for="'assigned-permission-' + permission.id"
                                          class="form-check-label"
                                       >
                                          {{ permission.name }}
                                       </label>
                                    </div>
                                 </div>
                              </transition>
                           </div>

                           <div
                              v-if="assignedPermissions.length === 0"
                              class="text-muted small"
                           >
                              No permissions assigned.
                           </div>
                        </div>
                     </div>
                  </div>

               </div>

               <div class="custom-tooltip">
                  Please select permissions.
                  <span class="tooltip-arrow"></span>
               </div>
            </div>
         </div>
      </div>
   </RecordFormModal>


   <h4 class="sa-core-color fw-semibold mb-2">
      <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
   </h4>
   <!--Tables-->
   <div class="card glass mt-0 bg-sa-card">
      <div class="card-body bg-sa-card-body p-0">
                  <!-- Table action row (shared: TableToolbar.vue) -->
         <TableToolbar
            can-add
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            :can-delete="$hasPermission('roles.delete')"
            @add="clearForm()"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
         >
            <!-- More menu -->
            <template #more>
               <li><h6 class="dropdown-header">Data</h6></li>
               <li>
                  <button type="button" class="dropdown-item" data-bs-toggle="modal" data-bs-target="#importModal">
                     <i class="fa fa-upload fa-fw me-1"></i> Import
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                     <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                     <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                     <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                  </button>
               </li>
            </template>
         </TableToolbar>
         <!-- Table Search Panel -->

         <!-- Table Search Panel -->
         <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
         />
         <!-- What the table is currently showing -->
         <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
         />
         <!-- Table -->
         <div class="table-responsive">
            
          <table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
      <thead>
      <tr>
         <th>
            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
         </th>
         <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
         <th class="button hide-sm-td">No</th>
         <SortableTh v-if="isColumnVisible('role')" column="role" :table-data="tableData" @sort="orderBy">Role</SortableTh>
         <th v-if="isColumnVisible('permissions')" class="hide-sm-td">Permissions</th>
         <SortableTh v-if="isColumnVisible('date_created')" column="date_created" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Date Created</SortableTh>
         <th class="button">Action</th>
      </tr>
   </thead>

   <tbody>
      <template v-for="(record, index) in tableData.records" :key="record.id">

         <!-- Main row -->
         <tr class="clickable-row"
             :data-record-id="record.id"
             :class="[
                { 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) },
                record.pinned_role ? 'pinned-row pinned-' + record.pinned_role : ''
             ]"
             @click="$onRowClick(record, $event)"
             @dblclick="$onRowDblClick(record, $event)"
         >
            <td>
               <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
            </td>
            <td class="expand-cell hide-sm-td"
                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                @click="$toggleDetails(record.id)">
               <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                  :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
            </td>
            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
            <td v-if="isColumnVisible('role')">
               <!-- Phones: + / − and the number go in front of the role (their columns are hidden) -->
               <span
                  class="show-sm-inline expand-cell me-2"
                  :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                  @click="$toggleDetails(record.id)"
               >
                  <i class="fa fa-lg expand-icon"
                     :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                     :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
               </span>
               <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>{{ record.role }}
               <!-- Pinned roles: small coloured label under the name (PINNED_ROLE_ROWS in api_roles_helpers.php) -->
               <div v-if="record.pinned_role" class="mt-1">
                  <span class="badge" :class="record.pinned_role === 'developer' ? 'bg-info text-dark' : 'bg-warning text-dark'">
                     Pinned to top
                  </span>
               </div>
            </td>
            <td v-if="isColumnVisible('permissions')" class="hide-sm-td">
               <span class="badge bg-info text-dark" :title="getPermissionNames(record.permissions)">
                  {{ getPermissionList(record.permissions).length }} permissions
               </span>
            </td>
            <td v-if="isColumnVisible('date_created')" class="hide-sm-td text-nowrap" :title="$ConvertToLocalDate(record.date_created)">{{ $friendlyDate(record.date_created) }}</td>
            <td>
               <RowActions
                  :record="record"
                  :modal-id="'deleteCrudModal' + record.id"
                  :can-soft-delete="$hasPermission('roles.delete')
                     && !protectedRoleIds.includes(Number(record.id))"
                  :can-restore="$hasPermission('roles.delete')"
                  :can-delete-permanently="$hasPermission('roles.delete')"
                  @edit="editRecord"
                  @delete-type="setDeleteType"
               />

               <!--Delete Modal-->
               <DeleteModal
                  :modal-id="'deleteCrudModal' + record.id"
                  :title="deleteModalTitle"
                  :message="deleteModalMessage"
                  @confirm="deleteRecord(record.id)"
               />
               <!--End Delete Modal-->
            </td>
         </tr>

         <!-- Hidden expanded row -->
         <tr class="collapse-row">
            <td class="p-0"></td>
            <td class="p-0 hide-sm-td"></td>
            <td colspan="5" class="p-0">
               <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                  <div class="p-3 details-list">
                     <div><i class="fa fa-hashtag"></i><strong>Role Number:</strong> {{ record.role_number }}</div>
                     <div><i class="fa fa-user"></i><strong>Creator Name:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                     <div><i class="fa fa-building"></i><strong>Creator Company:</strong> {{ record.company_name }}</div>
                     <div><i class="fa fa-sitemap"></i><strong>Creator Branch:</strong> {{ record.branch_name }}</div>

                     <!-- Permissions grouped by category, like the form -->
                     <div>
                        <i class="fa fa-shield"></i><strong>Permissions ({{ getPermissionList(record.permissions).length }}):</strong>
                        <div v-for="(perms, categoryName) in groupPermissionsByCategory(getPermissionList(record.permissions))"
                             :key="categoryName"
                             class="mt-2">
                           <div class="fw-semibold text-info small">{{ categoryName }} ({{ perms.length }})</div>
                           <div class="d-flex flex-wrap gap-1 mt-1">
                              <span v-for="p in perms" :key="p.id" class="badge bg-secondary">{{ p.name }}</span>
                           </div>
                        </div>
                        <span v-if="!getPermissionList(record.permissions).length" class="text-muted sa-core-color">None</span>
                     </div>

                     <!-- Shown on phones, or always when the column is hidden (Columns button) -->
                     <div :class="{ 'show-sm-td': isColumnVisible('role') }"><i class="fa fa-user-secret"></i><strong>Role:</strong> {{ record.role }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('date_created') }"><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>

                     <!-- Always in details -->
                     <div v-if="$hasPermission('view-location')"><i class="fa fa-location-arrow"></i><strong>Created From:</strong> {{ record.current_location }}</div>
                     <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ this.$ConvertToLocalDate(record.date_updated) }}</div>
                     <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                  </div>
               </div>
            </td>
         </tr>

      </template>

      <!-- No rows: blank while loading, then the empty message in the middle -->
      <tr v-if="tableData.records.length === 0" class="table-filler">
         <td colspan="7" class="text-center align-middle" :style="{ height: fullPageHeight() }">
            <TableEmptyState
               v-if="!tableLoading"
               :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
               :can-add="true"
               :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No roles yet'"
               :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Roles decide what each user can see and do.'"
               icon="fa fa-user-secret"
               @add="clearForm()"
               @clear="clearAllFilterChips"
            />
         </td>
      </tr>
   </tbody>

   <tfoot class="table-light">
      <tr>
         <td colspan="7">
            <!-- Pagination (hidden when there are no rows; its space is kept) -->
            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
            <Pagination
               v-else
               :currentPage="tableData.currentPage"
               :totalPages="tableData.totalPages"
               :hasNextPage="tableData.hasNextPage"
               :totalRecords="tableData.totalRecords"
               :startRecord="tableData.startRecord"
               :endRecord="tableData.endRecord"
               :showFullPagination="tableData.totalRecords > 0"
               :lastKnownPage="lastKnownPage"
               @page-changed="fetchRecords"
               @loaderMessage="updateLoaderMessage"
               @last-page-request="goToLastPage"
            />
         </td>
      </tr>
   </tfoot>
</table>            
                     
         </div>
         <!-- Bottom Controls: Delete & Pagination -->
      </div>
   </div>
   <!--End Tables-->
</template>
<script>
import PageStatus from "../../../components/admin/PageStatus.vue";
import TableToolbar from "../../../components/admin/TableToolbar.vue";
import FilterModal from "../../../components/admin/FilterModal.vue";
import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
import Pagination from "../../../components/admin/Pagination.vue";
import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
import SortableTh from "../../../components/admin/SortableTh.vue";
import DeleteModal from "../../../components/admin/DeleteModal.vue";
import RowActions from "../../../components/admin/RowActions.vue";
import SearchBox from "../../../components/admin/SearchBox.vue";
import ImportModal from "../../../components/admin/ImportModal.vue";
import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
import FormField from "../../../components/admin/FormField.vue";
import SelectField from "../../../components/admin/SelectField.vue";
import HiddenField from "../../../components/admin/HiddenField.vue";
import rolesVersion2Mixin from "./mixins/rolesVersion2Mixin.js";
import tableMixin from "@/mixins/tableMixin.js";
import axios from 'axios';


export default {
   name: 'RolesVersion2',
   mixins: [tableMixin, rolesVersion2Mixin], // shared table methods first, then this page's own
   components: {
      PageStatus,
      TableToolbar,
      FilterModal,
      ActiveFilters,
      Pagination,
      TableEmptyState,
      SortableTh,
      DeleteModal,
      RowActions,
      SearchBox,
      ImportModal,
      RecordFormModal,
      FormField,
      SelectField,
      HiddenField,
   },
   data() {
      return {
         // Standard data (isSaving, tableData, defaultAlert, formImportData ...) comes from tableMixin
         standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
         apiUrl: "/users/api_roles.php",   // used by tableMixin's fetchRecords, delete, import, export
         importTemplateFileName: "roles_import_template.xlsx",
         columnsKey: "rolesv2",            // saves this page's hidden columns (Columns button)
         formInputs: this.getDefaultFormInputs(),
         permissionOptions: [],
         permissionsLoaded: false,
         protectedRoleIds: [1, 2], // System Administrator, Developer: never deleted

         availablePermissions: [],
         assignedPermissions: [],
         selectedAvailablePermissions: {},
         selectedAssignedPermissions: {},

         expandedAvailablePermissionCategories: [],
         expandedAssignedPermissionCategories: [],

         exportFileName: "rolesExport", // name of the exported file
         pageIcon: 'fa fa-user-secret', // same icon as this page in the sidebar
         // Filter window: View
         viewOptions: [
            { label: "All Records", value: "all" },
            { label: "Records By Me", value: "byme" },
            { label: "Records Deleted", value: "deletedone" },
         ],
         // Filter window: Search By
         searchByOptions: [
            { label: "Role", value: "role" },
            { label: "Role Number", value: "role_number" },
         ],
      };
   },
      mounted(){
      this.fetchPermissionOptions(); // permission list for the picker and the table
   },

   async created() {      
      //alert(this.$store.state.isLoggedIn);
      //this.retriveUserDetails();           
      await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
      this.$checkIfCanFetchLocation();//Only applies if location is enabled for user. If not Log user out for him to enable location 
   },
   computed: {
    // Page heading that follows the chosen filter.
      pageTitle() {
          const titles = {
              all: "Roles",
              byme: "Roles By Me",
              deletedone: "Roles Deleted Records",
          };
          return titles[this.tableData.recordFilter] || "Roles";
      },
      // Columns the user can show or hide (Columns button)
      tableColumns() {
          return [
              { key: "role", label: "Role" },
              { key: "permissions", label: "Permissions" },
              { key: "date_created", label: "Date Created" },
          ];
      },
     groupedAvailablePermissions() {
        return this.groupPermissionsByCategory(this.availablePermissions);
     },
     groupedAssignedPermissions() {
        return this.groupPermissionsByCategory(this.assignedPermissions);
     }, 
   },
   methods: {
      // "3,5,8" or [3,5,8] → [3, 5, 8] (numbers). Empty or null → []
      parsePermissions(value) {
         if (Array.isArray(value)) {
            return value.map(Number).filter(n => !isNaN(n) && n > 0);
         }
         if (typeof value === "string" && value.trim() !== "") {
            return value.split(",").map(p => Number(p.trim())).filter(n => !isNaN(n) && n > 0);
         }
         return [];
      },
      // Permission objects for a role. Number() on both sides because the API sends IDs as strings
      getPermissionList(value) {
         const ids = this.parsePermissions(value);
         return this.permissionOptions.filter(option => ids.includes(Number(option.id)));
      },
      getPermissionNames(value) {
         return this.getPermissionList(value).map(p => p.name).join(", ");
      },
      // Reset button: empty the form but keep the id (Update still targets the same role),
      // and move every permission back to the Available side so the picker matches.
      resetForm() {
         const id = this.formInputs.id;
         this.formInputs = { ...this.getDefaultFormInputs(), id };

         this.assignedPermissions = [];
         this.availablePermissions = [...this.permissionOptions];

         this.selectedAvailablePermissions = {};
         this.selectedAssignedPermissions = {};
         this.expandedAvailablePermissionCategories = [];
         this.expandedAssignedPermissionCategories = [];

         this.submitted = false;
         this.$clearValidationStates('#recordForm');
      },
      fetchPermissionOptions(){
         let url = `${this.apiUrl}?action=fetchPermissions`;

         axios.get(url,this.$handleConfig())
            .then((response) => {
               if (response.data.status=="success") {
                  this.handleFetchPermissionsSuccess(response);
                  this.permissionsLoaded = true;
               } else {
                  this.handleAlert("starting",response.data.message,"danger","page");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","page");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","page");
                }
               
            });
      },
      // Selection bar: System Administrator and Developer (protectedRoleIds) are never deleted.
      // Take them out of the ticked rows, then run the shared bulk action (tableMixin).
      runBulkAction(deleteType = "soft") {
         if (deleteType === "soft") {
            const kept = this.tableData.selectedRecordIds.filter((id) => !this.protectedRoleIds.includes(Number(id)));
            if (!kept.length) {
               this.handleAlert("ending", "System Administrator and Developer can't be deleted.", "warning", "page");
               return;
            }
            this.tableData.selectedRecordIds = kept;
         }
         return tableMixin.methods.runBulkAction.call(this, deleteType);
      },
      getDefaultFormInputs(){
         return {
            //Form Data
            id: "",
            role: "",
            permissions: [],
            selectedPermissions: []
         };
      },
            // Table defaults: the standard ones (tableMixin) + this page's own
      getDefaultTableData(){
         return {
            ...this.baseTableData(),
            searchBy: 'role',
            searchPlaceHolderText: 'Search by role',
         };
      },
      addSampleData() {
         // First 3 loaded permissions go to the "Assigned" list
         const sampleIds = this.permissionOptions.slice(0, 3).map(p => Number(p.id));

         this.assignedPermissions = this.permissionOptions.filter(p =>
            sampleIds.includes(Number(p.id))
         );
         this.availablePermissions = this.permissionOptions.filter(p =>
            !sampleIds.includes(Number(p.id))
         );

         this.formInputs = {
            ...this.getDefaultFormInputs(),
            // Suffix so saving the sample twice doesn't clash if role names must be unique
            role: "Sales Manager " + Math.floor(100 + Math.random() * 900),
            permissions: [...this.assignedPermissions],
         };

         // Clear any ticked boxes / open categories from before
         this.selectedAvailablePermissions = {};
         this.selectedAssignedPermissions = {};
         this.expandedAvailablePermissionCategories = [];
         this.expandedAssignedPermissionCategories = [];

         this.submitted = false;
         this.$clearValidationStates("#recordForm");
         this.$scrollToTopModal("recordFormModal");
      },
      saveOrEditRecord() {    
      if (this.isSaving) return;
      // Never save before the permission list has loaded (it would save the role with no permissions)
      if (!this.permissionsLoaded) {
         this.handleAlert("starting", "Permissions are still loading. Please close and open the form again.", "danger", "modal");
         return;
      }

      this.isSaving = true;

      try {     
         this.$scrollToTopModal("recordFormModal"); 
         this.submitted = true;
         const formIsValid = this.$validateForm('#recordForm');
         if (!formIsValid) { 
              this.$playErrorSound();
              this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
              this.isSaving = false;
              return; 
        }

         this.handleAlert("starting","Please wait submiting","info","modal");

         let formData = this.formData_fields();

         if (!this.formInputs.id) {
            this.tableData.currentPage = 1;
         }

         //console.log(this.formInputs.permissions);
         //return;
         const start = performance.now(); // Start time
         // Perform the POST request
         axios.post(this.apiUrl, formData, this.$handleConfig())
            .then((response) => {     
               if (response.data.status =='success') {
                  const end = performance.now(); // End time
                  const duration = end - start;
                  console.log("Time taken to receive:" + duration.toFixed(2));
                  this.handleSaveOrEditSuccess(response);
               } else {
                   this.handleError("starting", response.data.message[0], "danger", "modal");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","modal");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","modal");
                }
               //this.handleError("starting",error,"danger","modal");
             })
            .finally(() => {
                this.isSaving = false;
            });} catch (error) {
            this.isSaving = false;
            throw error;
        }
      },
      formData_fields(){
         let formData = new FormData();
         formData.append("id", this.formInputs.id);
         formData.append("role", this.formInputs.role);
         //formData.append("permissions", this.formInputs.selectedPermissions);

         // ✅ Convert objects → IDs
         const permissionIds = this.formInputs.permissions.map(p => p.id);
         // Send as comma-separated string (common backend pattern)
         formData.append("permissions", permissionIds.join(','));

         formData.append("latitude", this.$store.state.latitude);
         formData.append("longitude", this.$store.state.longitude);
         formData.append('action', 'saveOrEditRecord');
         return formData;
      },
      editRecord(records) {
         this.isEditing = true;
         this.$clearValidationStates('#recordForm');

         this.formInputs.id = records.id ?? '';
         this.formInputs.role = records.role ?? '';

         const permissionIds = this.parsePermissions(records.permissions);

         this.formInputs.permissions = this.permissionOptions.filter(permission =>
            permissionIds.includes(Number(permission.id))
         );

         this.assignedPermissions = [...this.formInputs.permissions];

         this.availablePermissions = this.permissionOptions.filter(permission =>
            !permissionIds.includes(Number(permission.id))
         );

         this.selectedAvailablePermissions = {};
         this.selectedAssignedPermissions = {};
         this.expandedAvailablePermissionCategories = [];
         this.expandedAssignedPermissionCategories = [];
      },
         },
}; 
</script>